import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cx } from './cx';

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * Envelope de campo: rótulo associado, dica e erro ligados por
 * `aria-describedby`. Centralizar isso é o que garante que nenhum campo novo
 * nasça sem rótulo acessível (RNF-F06).
 */
function FieldShell({ label, hint, error, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
      </label>
      {children({ id, describedBy })}
      {hint && !error && (
        <p id={hintId} className="text-xs text-text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL_CLASSES =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted disabled:opacity-60';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({ label, hint, error, className, ...rest }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={cx(CONTROL_CLASSES, error && 'border-danger', className)}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
}

export function SelectField({ label, hint, error, options, className, ...rest }: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <select
          id={id}
          aria-describedby={describedBy}
          className={cx(CONTROL_CLASSES, className)}
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export interface ToggleGroupProps<T extends string> {
  label: string;
  hint?: string;
  options: Array<{ value: T; label: string }>;
  value: T[];
  onChange: (value: T[]) => void;
  /** `false` permite seleção múltipla (plataformas). */
  single?: boolean;
}

/**
 * Grupo de opções em botões. Usa `aria-pressed` em vez de checkbox escondido:
 * o leitor de tela anuncia o estado e o teclado funciona sem CSS extra.
 */
export function ToggleGroup<T extends string>({
  label,
  hint,
  options,
  value,
  onChange,
  single = false,
}: ToggleGroupProps<T>) {
  const toggle = (option: T): void => {
    if (single) {
      onChange([option]);
      return;
    }
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-text">{label}</span>
      <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(option.value)}
              className={cx(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                active
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border bg-surface text-text-muted hover:bg-surface-raised',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  /** Esconde o rótulo visualmente, mantendo-o para leitor de tela. */
  hideLabel?: boolean;
}

/**
 * Caixa de seleção. Fora do `FieldShell` de propósito: aqui o rótulo fica ao
 * lado do controle, não acima dele. O rótulo é obrigatório mesmo quando
 * invisível — numa lista, "selecionar" sem dizer o quê não ajuda ninguém
 * (RNF-EU03).
 */
export function Checkbox({ label, hideLabel = false, className, ...rest }: CheckboxProps) {
  const id = useId();

  return (
    <span className="inline-flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className={cx('h-4 w-4 rounded-sm border-border-strong accent-accent', className)}
        {...rest}
      />
      <label htmlFor={id} className={cx('text-sm text-text', hideLabel && 'sr-only')}>
        {label}
      </label>
    </span>
  );
}
